import React, { useState, useEffect } from 'react';
import { Typography, Chip, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem, TextField, FormControl, InputLabel, Select } from '@mui/material';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';

import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { toastError, toastSuccess } from '../utils/toast';

// Logos
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';

const PROVIDER_OPTIONS = [
  { value: 'AWS', label: 'AWS' },
  { value: 'GCP', label: 'Google Cloud' },
  { value: 'AZURE', label: 'Azure' },
  { value: 'OPENAI', label: 'OpenAI' },
  { value: 'ANTHROPIC', label: 'Anthropic' },
  { value: 'OTHER', label: 'Other' },
];

export default function ResourceAPIKeys() {
  const { authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const selectedWorkspaceId = useSelectedWorkspace();

  const [apiKeys, setApiKeys] = useState<ApiTypes.ProviderApiKeyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedKeyForMenu, setSelectedKeyForMenu] = useState<ApiTypes.ProviderApiKeyDTO | null>(null);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiTypes.ProviderApiKeyDTO | null>(null);

  // Form states
  const [formProvider, setFormProvider] = useState('AWS');
  const [formKey, setFormKey] = useState('');
  const [formSecret, setFormSecret] = useState('');
  const [formEndpoint, setFormEndpoint] = useState('');
  const [formRegion, setFormRegion] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, [selectedWorkspaceId]);

  const loadApiKeys = async () => {
    if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') {
      setApiKeys([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await withTokenRefresh(() => apiClient.getApiKeys(selectedWorkspaceId));
      if (response.success && response.apiKeys) {
        setApiKeys(response.apiKeys);
      } else {
        setApiKeys([]);
      }
    } catch (error: any) {
      console.error('[ResourceAPIKeys] Error loading API keys:', error);
      toastError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'AWS':
        return <img src={awsLogo} alt="AWS" style={{ width: 24, height: 24 }} />;
      case 'GCP':
        return <img src={googleCloudLogo} alt="GCP" style={{ width: 24, height: 24 }} />;
      case 'ANTHROPIC':
        return <img src={anthropicLogo} alt="Anthropic" style={{ width: 24, height: 24 }} />;
      case 'OPENAI':
        return (
          <svg viewBox="0 0 24 24" style={{ width: 24, height: 24 }} fill="#10A37F">
            <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
          </svg>
        );
      default:
        return (
          <div className="w-6 h-6 rounded-md bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
            {provider.substring(0, 1)}
          </div>
        );
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, key: ApiTypes.ProviderApiKeyDTO) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedKeyForMenu(key);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setFormProvider('AWS');
    setFormKey('');
    setFormSecret('');
    setFormEndpoint('');
    setFormRegion('');
    setModalOpen(true);
  };

  const openEditModal = () => {
    if (!selectedKeyForMenu) return;
    setIsEditing(true);
    setFormProvider(selectedKeyForMenu.provider);
    setFormKey(selectedKeyForMenu.key);
    setFormSecret(''); // Do not populate secret for security
    setFormEndpoint(selectedKeyForMenu.endpoint || '');
    setFormRegion(selectedKeyForMenu.region || '');
    setModalOpen(true);
    handleMenuClose();
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') {
      toastError('Please select a specific workspace first');
      return;
    }
    if (!formKey) {
      toastError('Key identifier is required');
      return;
    }
    if (!isEditing && !formSecret) {
      toastError('Secret is required for new API keys');
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing && selectedKeyForMenu) {
        const payload: ApiTypes.UpdateProviderApiKeyRequest = {
          provider: formProvider,
          key: formKey,
          endpoint: formEndpoint || undefined,
          region: formRegion || undefined,
        };
        // Only include secret if the user typed something new
        if (formSecret) {
          payload.secret = formSecret;
        }

        const res = await withTokenRefresh(() =>
          apiClient.updateApiKey(selectedKeyForMenu.id, payload)
        );
        if (res.success) {
          toastSuccess('API Key updated successfully');
          setModalOpen(false);
          loadApiKeys();
        } else {
          toastError(res.message || 'Failed to update API key');
        }
      } else {
        const payload: ApiTypes.CreateProviderApiKeyRequest = {
          workspaceId: selectedWorkspaceId,
          provider: formProvider,
          key: formKey,
          secret: formSecret,
          endpoint: formEndpoint || undefined,
          region: formRegion || undefined,
        };

        const res = await withTokenRefresh(() => apiClient.createApiKey(payload));
        if (res.success) {
          toastSuccess('API Key created successfully');
          setModalOpen(false);
          loadApiKeys();
        } else {
          toastError(res.message || 'Failed to create API key');
        }
      }
    } catch (error: any) {
      console.error('[ResourceAPIKeys] Error saving API key:', error);
      toastError(error.message || 'An error occurred while saving the API key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (selectedKeyForMenu) {
      setKeyToDelete(selectedKeyForMenu);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;

    if ((keyToDelete.bucket_count || 0) > 0 || (keyToDelete.aillm_count || 0) > 0) {
      toastError('Cannot delete: Key is actively used by resources');
      setDeleteDialogOpen(false);
      return;
    }

    try {
      const res = await withTokenRefresh(() => apiClient.deleteApiKey(keyToDelete.id));
      if (res.success) {
        toastSuccess('API Key deleted successfully');
        loadApiKeys();
      } else {
        toastError(res.message || 'Failed to delete API key');
      }
    } catch (error: any) {
      console.error('[ResourceAPIKeys] Error deleting API key:', error);
      toastError(error.message || 'Failed to delete API key');
    } finally {
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    }
  };

  const filteredKeys = apiKeys.filter((key) => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      key.provider.toLowerCase().includes(lowerQuery) ||
      key.key.toLowerCase().includes(lowerQuery) ||
      (key.endpoint && key.endpoint.toLowerCase().includes(lowerQuery)) ||
      (key.region && key.region.toLowerCase().includes(lowerQuery))
    );
  });

  if (selectedWorkspaceId === 'ALL') {
    return (
      <div className="w-full h-full flex items-center justify-center p-8 bg-gray-50">
        <Typography variant="h6" color="textSecondary">
          Please select a specific workspace to manage its Resource API Keys.
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col p-8 bg-gray-50">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '30px',
              lineHeight: '38px',
              color: '#101828',
            }}
          >
            Resource API Keys
          </Typography>
          <Typography
            style={{
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#475467',
              marginTop: '4px',
            }}
          >
            Manage provider credentials securely reused across storage units and AI models.
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadApiKeys}
            className="flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
            }}
            title="Refresh"
            disabled={loading}
          >
            <RefreshIcon sx={{ fontSize: 20 }} className={loading ? 'animate-spin' : ''} />
          </button>
          
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
            }}
            title="Search"
          >
            <SearchIcon sx={{ fontSize: 20 }} />
          </button>

          <button
            onClick={openCreateModal}
            className="flex items-center justify-center gap-2 text-white hover:opacity-90 transition-colors"
            style={{
              height: '40px',
              padding: '0 16px',
              borderRadius: '8px',
              backgroundColor: '#617BFF',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Add API Key
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by provider, key, region, endpoint..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ fontSize: '14px' }}
          />
        </div>
      )}

      {/* Grid of API Keys */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Typography color="textSecondary">Loading...</Typography>
        </div>
      ) : filteredKeys.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No API Keys found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {searchQuery ? "Try adjusting your search query." : "You haven't added any provider API keys yet."}
          </Typography>
          {!searchQuery && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal} sx={{ bgcolor: '#617BFF' }}>
              Add API Key
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredKeys.map((key) => (
            <div
              key={key.id}
              className="border border-gray-200 hover:border-gray-300 transition-colors rounded-xl bg-white flex flex-col relative"
              style={{ overflow: 'hidden' }}
            >
              {/* Card Header */}
              <div className="p-5 flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shadow-sm">
                    {getProviderIcon(key.provider)}
                  </div>
                  <div>
                    <Typography style={{ fontWeight: 600, color: '#101828', fontSize: '16px' }}>
                      {key.provider}
                    </Typography>
                    <Typography style={{ fontSize: '12px', color: '#667085' }}>
                      Added {new Date(key.createdAt).toLocaleDateString()}
                    </Typography>
                  </div>
                </div>
                
                <IconButton size="small" onClick={(e) => handleMenuClick(e, key)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>

              {/* Card Body */}
              <div className="p-5 flex flex-col gap-4 flex-1">
                <div>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#667085', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Access Key / Identifier
                  </Typography>
                  <Typography style={{ fontSize: '14px', color: '#344054', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    {key.key}
                  </Typography>
                </div>
                
                {(key.endpoint || key.region) && (
                  <div className="flex flex-wrap gap-2">
                    {key.region && (
                      <Chip
                        label={`Region: ${key.region}`}
                        size="small"
                        style={{ backgroundColor: '#F2F4F7', color: '#344054', fontSize: '12px' }}
                      />
                    )}
                    {key.endpoint && (
                      <Chip
                        label={`Endpoint: ${key.endpoint}`}
                        size="small"
                        style={{ backgroundColor: '#F2F4F7', color: '#344054', fontSize: '12px' }}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <div className="flex gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5" title="Storage Units (Buckets) using this key">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                    <span style={{ fontWeight: 600, color: key.bucket_count ? '#101828' : '#98A2B3' }}>
                      {key.bucket_count || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" title="AI Models using this key">
                    <MemoryIcon sx={{ fontSize: 16, color: 'currentColor' }} />
                    <span style={{ fontWeight: 600, color: key.aillm_count ? '#101828' : '#98A2B3' }}>
                      {key.aillm_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          style: { borderRadius: 8, minWidth: 150, marginTop: 8 },
        }}
      >
        <MenuItem onClick={openEditModal} style={{ fontSize: '14px', padding: '10px 16px' }}>
          <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#667085' }} />
          Edit Key
        </MenuItem>
        <MenuItem 
          onClick={handleDeleteClick} 
          style={{ fontSize: '14px', padding: '10px 16px', color: '#D92D20' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          Delete Key
        </MenuItem>
      </Menu>

      {/* Create / Edit Dialog */}
      <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 600 }}>
          {isEditing ? 'Edit API Key' : 'Add API Key'}
        </DialogTitle>
        <DialogContent dividers>
          <div className="space-y-5 py-2">
            <FormControl fullWidth size="small">
              <InputLabel>Provider</InputLabel>
              <Select
                value={formProvider}
                label="Provider"
                onChange={(e) => setFormProvider(e.target.value)}
              >
                {PROVIDER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Access Key / Identifier"
              size="small"
              value={formKey}
              onChange={(e) => setFormKey(e.target.value)}
              placeholder="e.g. AKIAIOSFODNN7EXAMPLE"
              required
            />

            <TextField
              fullWidth
              label="Secret Key / Password"
              size="small"
              type="password"
              value={formSecret}
              onChange={(e) => setFormSecret(e.target.value)}
              placeholder={isEditing ? "Leave blank to keep current secret" : "Required"}
              required={!isEditing}
            />

            <div className="grid grid-cols-2 gap-4">
              <TextField
                fullWidth
                label="Region (Optional)"
                size="small"
                value={formRegion}
                onChange={(e) => setFormRegion(e.target.value)}
                placeholder="e.g. us-east-1"
              />
              <TextField
                fullWidth
                label="Endpoint (Optional)"
                size="small"
                value={formEndpoint}
                onChange={(e) => setFormEndpoint(e.target.value)}
                placeholder="e.g. s3.amazonaws.com"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleModalClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ bgcolor: '#617BFF', '&:hover': { bgcolor: '#4B63E6' } }}
            disabled={submitting || !formKey || (!isEditing && !formSecret)}
          >
            {submitting ? 'Saving...' : 'Save API Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle style={{ fontWeight: 600, color: '#D92D20' }}>
          Delete API Key
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the API key <strong>{keyToDelete?.key}</strong>?
            This action cannot be undone.
          </Typography>
          {((keyToDelete?.bucket_count || 0) > 0 || (keyToDelete?.aillm_count || 0) > 0) && (
            <Box mt={2} p={2} bgcolor="#FEF3F2" borderRadius={1} border="1px solid #FECDCA">
              <Typography color="#B42318" fontWeight={500} variant="body2">
                Warning: This key is currently in use!
              </Typography>
              <Typography color="#D92D20" variant="body2" mt={0.5}>
                It is linked to {keyToDelete?.bucket_count || 0} bucket(s) and {keyToDelete?.aillm_count || 0} AI model(s). You must remove or reconfigure these resources before deleting this key.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={((keyToDelete?.bucket_count || 0) > 0) || ((keyToDelete?.aillm_count || 0) > 0)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
